import { useEffect, useRef, useState } from "react";
import {
  createSpeechRecognizer,
  isSpeechRecognitionAvailable,
  type SpeechRecognizer,
} from "../util/speech";

interface Props {
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
  minHeightClass?: string;
  disabled?: boolean;
  /**
   * Hide the small "audio may leave the device" disclosure. Useful when the
   * surrounding screen already explains the trade-off.
   */
  hideDisclosure?: boolean;
}

export function VoiceTextarea(props: Props) {
  const [recording, setRecording] = useState(false);
  const [interim, setInterim] = useState("");
  const [voiceError, setVoiceError] = useState<string | null>(null);
  const recognizerRef = useRef<SpeechRecognizer | null>(null);
  const voiceAvailable = isSpeechRecognitionAvailable();

  useEffect(() => {
    return () => {
      recognizerRef.current?.stop();
      recognizerRef.current = null;
    };
  }, []);

  const handleMicToggle = () => {
    if (recording) {
      recognizerRef.current?.stop();
      return;
    }
    setVoiceError(null);
    const recognizer = createSpeechRecognizer({
      onStart: () => setRecording(true),
      onEnd: () => {
        setRecording(false);
        setInterim("");
        recognizerRef.current = null;
      },
      onError: (msg) => {
        setVoiceError(msg);
        setRecording(false);
        setInterim("");
      },
      onPartial: setInterim,
      onFinal: (text) => {
        const next =
          props.value.trim().length === 0 ? text : `${props.value.trimEnd()} ${text}`;
        props.onChange(next);
        setInterim("");
      },
    });
    if (!recognizer) {
      setVoiceError("Speech recognition isn't available in this browser.");
      return;
    }
    recognizerRef.current = recognizer;
    recognizer.start();
  };

  const minHeight = props.minHeightClass ?? "min-h-[140px]";

  return (
    <div className="flex flex-col gap-2">
      <div className="relative">
        <textarea
          className={`input ${minHeight} resize-y pr-12`}
          placeholder={props.placeholder}
          value={props.value}
          disabled={props.disabled}
          onChange={(e) => props.onChange(e.target.value)}
        />
        {voiceAvailable && (
          <button
            type="button"
            onClick={handleMicToggle}
            aria-pressed={recording}
            aria-label={recording ? "Stop voice input" : "Start voice input"}
            disabled={props.disabled}
            className={`absolute right-2 top-2 inline-flex h-9 w-9 items-center justify-center rounded-full transition ${
              recording
                ? "animate-pulse bg-rose-600 text-white"
                : "bg-accent-soft text-accent hover:bg-accent/15"
            }`}
          >
            <MicIcon active={recording} />
          </button>
        )}
      </div>

      {recording && (
        <div className="rounded-xl bg-rose-50 px-3 py-2 text-xs text-rose-900 ring-1 ring-rose-200">
          <div className="font-semibold">Listening… tap the mic again to stop.</div>
          {interim.length > 0 ? (
            <div className="mt-1 italic">{interim}</div>
          ) : (
            <div className="mt-1 italic text-rose-700/80">(speak now)</div>
          )}
        </div>
      )}

      {voiceError && !recording && (
        <div className="rounded-xl bg-rose-50 px-3 py-2 text-xs text-rose-900 ring-1 ring-rose-200">
          Voice input failed: {voiceError}
        </div>
      )}

      {voiceAvailable && !props.hideDisclosure && (
        <p className="text-xs text-ink-muted">
          <strong>Heads-up:</strong> the browser's built-in speech recognition may send audio to
          your browser vendor's servers (e.g. Google on Chrome). Type instead if that's a concern.
        </p>
      )}
    </div>
  );
}

function MicIcon(props: { active: boolean }) {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M12 14a3 3 0 003-3V6a3 3 0 10-6 0v5a3 3 0 003 3z" />
      <path d="M19 11a1 1 0 10-2 0 5 5 0 01-10 0 1 1 0 10-2 0 7 7 0 006 6.93V20H8a1 1 0 100 2h8a1 1 0 100-2h-3v-2.07A7 7 0 0019 11z" />
      {props.active && (
        <circle cx="20" cy="4" r="2.5" fill="#fff" stroke="currentColor" strokeWidth="1" />
      )}
    </svg>
  );
}
